<template>
    <div class="w-full">
        <!-- Mobile Header -->
        <div class="md:hidden flex flex-col items-start gap-2">
            <p class="text-slate-400 text-sm font-normal leading-5">{{ program.formatted_type }}</p>
            <h1 class="text-slate-800 dark:text-slate-100 text-3xl font-semibold leading-9">{{ program.name }}</h1>

            <div class="mt-6 flex flex-col items-stretch gap-3 w-full">
                <!-- Favorite button -->
                <button
                    @click="$emit('toggle-favorite')"
                    class="flex h-11 px-4 items-center justify-center gap-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 w-full"
                >
                    <Icon
                        :icon="program.is_favorite ? 'heart' : 'unfavorited'"
                        :class="['w-5 h-5', program.is_favorite ? 'text-red-500' : 'text-slate-800 dark:text-slate-100']"
                    />
                    <span class="text-sm font-medium leading-5 text-slate-800 dark:text-slate-100">
                        {{ program.is_favorite ? 'Added to favorites' : 'Save to favorites' }}
                    </span>
                </button>

                <!-- Bonus alert button -->
                <div v-if="alertsFeatureEnabled" class="relative">
                    <button
                        @click="$emit('update:openBonusAlert', openBonusAlert === 'mobile' ? null : 'mobile')"
                        class="bonus-alert-button flex h-11 px-4 items-center justify-center gap-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 w-full"
                    >
                        <Icon :icon="bonusAlertEnabled ? 'bell-added-color' : 'alert'" class="w-5 h-5 text-slate-800 dark:text-slate-100" />
                        <span class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5">
                            {{ bonusAlertEnabled ? 'Bonus alert enabled' : 'Add bonus alert' }}
                        </span>
                    </button>
                    <div
                        v-if="openBonusAlert === 'mobile'"
                        class="hidden md:block bonus-alert-dropdown absolute left-0 right-0 top-12 mx-auto w-full max-w-[320px] p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg z-50"
                    >
                        <BonusAlertDropdown
                            :programName="program.name"
                            :email="email"
                            :bonusAlertEnabled="bonusAlertEnabled"
                            :bonusAlertLoading="bonusAlertLoading"
                            @toggle="$emit('toggle-bonus-alert', $event)"
                        />
                    </div>
                </div>
            </div>
        </div>

        <!-- Desktop Header -->
        <div class="hidden md:block self-stretch">
            <div class="flex pb-12 items-start self-stretch">
                <div class="flex items-start gap-2 rounded">
                    <button
                        @click="$emit('go-back')"
                        class="flex items-center gap-2 text-slate-800 dark:text-slate-100 hover:text-slate-600 dark:hover:text-slate-300 transition-colors duration-200 rounded"
                    >
                        <Icon icon="arrow-left-small" class="w-5 h-5" />
                        <span class="text-sm font-medium leading-5">Transfer partners guide</span>
                    </button>
                </div>
            </div>

            <div class="flex items-start justify-between gap-12 self-stretch">
                <div class="flex flex-col items-start gap-2 flex-1">
                    <p class="text-slate-400 text-base font-normal leading-6">{{ program.formatted_type }}</p>
                    <h1 class="text-slate-800 dark:text-slate-100 text-4xl font-semibold leading-10">{{ program.name }}</h1>
                </div>

                <div class="flex flex-wrap justify-end pt-8 items-center gap-1 md:gap-2 relative z-20">
                <!-- Favorite button -->
                <div class="relative">
                    <button
                        @click="$emit('toggle-favorite')"
                        @mouseover="showFavoriteTooltip = true"
                        @mouseleave="showFavoriteTooltip = false"
                        :class="[
                            'flex h-10 py-2 px-3 md:px-4 items-center gap-1 md:gap-2 rounded border shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200 w-[264px] lg:w-auto justify-center',
                            'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700',
                            !program.is_favorite && 'focus:ring-blue-500',
                        ]"
                    >
                        <Icon
                            :icon="program.is_favorite ? 'heart' : 'unfavorited'"
                            :class="['w-4 h-4', program.is_favorite ? 'text-red-500' : 'text-slate-800 dark:text-slate-100']"
                        />
                        <span class="text-xs lg:text-sm font-medium leading-5 whitespace-nowrap text-slate-800 dark:text-slate-100">
                            {{ program.is_favorite ? 'Added to Favorites' : 'Save to favorites' }}
                        </span>
                    </button>
                    <div
                        v-show="showFavoriteTooltip"
                        class="hidden md:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 py-1 px-2 text-sm text-white bg-[#1E293B] rounded-lg shadow-sm whitespace-nowrap z-50"
                    >
                        {{ program.is_favorite ? 'Remove from favorites' : 'Add program to favorites' }}
                    </div>
                </div>

                <!-- Bonus alert button -->
                <div v-if="alertsFeatureEnabled" class="relative">
                    <button
                        @click="$emit('update:openBonusAlert', openBonusAlert === 'hero-desktop' ? null : 'hero-desktop')"
                        @mouseover="showBonusAlertTooltip = true"
                        @mouseleave="showBonusAlertTooltip = false"
                        class="bonus-alert-button flex h-10 py-2 px-3 md:px-4 items-center gap-1 md:gap-2 rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 w-[264px] lg:w-auto justify-center"
                    >
                        <Icon :icon="bonusAlertEnabled ? 'bell-added-color' : 'alert'" class="w-4 h-4 text-slate-800 dark:text-slate-100" />
                        <span class="text-slate-800 dark:text-slate-100 text-xs lg:text-sm font-medium leading-5 whitespace-nowrap">
                            {{ bonusAlertEnabled ? 'Bonus alert enabled' : 'Add bonus alert' }}
                        </span>
                    </button>
                    <div
                        v-show="showBonusAlertTooltip"
                        class="hidden md:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 py-1 px-2 text-sm text-white bg-[#1E293B] rounded-lg shadow-sm whitespace-nowrap z-50"
                    >
                        {{ bonusAlertEnabled ? 'Turn off transfer bonus alerts' : 'Get transfer bonus alerts' }}
                    </div>
                    <div
                        v-if="openBonusAlert === 'hero-desktop'"
                        class="bonus-alert-dropdown absolute right-0 top-12 w-[264px] p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg z-50"
                    >
                        <BonusAlertDropdown
                            :programName="program.name"
                            :email="email"
                            :bonusAlertEnabled="bonusAlertEnabled"
                            :bonusAlertLoading="bonusAlertLoading"
                            variant="hero-desktop"
                            @toggle="$emit('toggle-bonus-alert', $event)"
                        />
                    </div>
                </div>
            </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';
import BonusAlertDropdown from './BonusAlertDropdown.vue';

defineProps({
    program: { type: Object, required: true },
    alertsFeatureEnabled: Boolean,
    bonusAlertEnabled: Boolean,
    bonusAlertLoading: Boolean,
    openBonusAlert: [String, null],
    email: String,
});

defineEmits(['go-back', 'toggle-favorite', 'toggle-bonus-alert', 'update:openBonusAlert']);

const showFavoriteTooltip = ref(false);
const showBonusAlertTooltip = ref(false);
</script>
